import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import Navigation from '../components/Navigation';
import Logo from '../components/Logo';
import { ListFooter } from '../ui';

const inputStyle = { width: '100%', padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const labelStyle = { fontSize: 11, color: '#888', fontWeight: 600, display: 'block', marginBottom: 4, textTransform: 'uppercase' };

function FabricAutocomplete({ value, onChange, onSelect, placeholder }) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef();
  const selecting = useRef(false);
  useEffect(() => { setQuery(value || ''); }, [value]);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const search = async (q) => {
    if (selecting.current) return;
    setQuery(q);
    if (q.length < 1) { setResults([]); setOpen(false); return; }
    setLoading(true);
    try {
      const r = await api.get('/oe/fabric-master?search=' + encodeURIComponent(q));
      setResults(r.data.data || []); setOpen(true);
    } catch { setResults([]); } finally { setLoading(false); }
  };
  const select = (fabric) => {
    selecting.current = true;
    setQuery(fabric.fabric_name);
    setOpen(false);
    onSelect && onSelect(fabric);
    setTimeout(() => { selecting.current = false; }, 100);
  };
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input style={inputStyle} value={query} onChange={e => search(e.target.value)}
        onFocus={() => query.length > 0 && results.length > 0 && setOpen(true)}
        placeholder={placeholder || 'Search fabric master...'} autoComplete="off" />
      {loading && <div style={{ position: 'absolute', right: 10, top: 9, fontSize: 11, color: '#aaa' }}>…</div>}
      {open && results.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, background: 'white', border: '1px solid #ddd', borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto' }}>
          {results.map(f => (
            <div key={f.id} onMouseDown={() => select(f)}
              style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f5f5f5', fontSize: 12 }}
              onMouseEnter={e => e.currentTarget.style.background = '#f5f5ff'}
              onMouseLeave={e => e.currentTarget.style.background = 'white'}>
              <div style={{ fontWeight: 600, color: '#222' }}>{f.fabric_name}</div>
              <div style={{ color: '#888', fontSize: 11, marginTop: 1 }}>
                {[f.composition, f.gsm && f.gsm + ' GSM', f.dia_width].filter(Boolean).join(' · ')}
              </div>
            </div>
          ))}
        </div>
      )}
      {open && results.length === 0 && !loading && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, background: 'white', border: '1px solid #ddd', borderRadius: 6, padding: '10px 12px', fontSize: 12, color: '#aaa' }}>No fabrics found</div>
      )}
    </div>
  );
}

function FabricRow({ row, idx, onChange, onRemove }) {
  const update = (field, val) => onChange(idx, { ...row, [field]: val });
  const handleFabricSelect = (fabric) => {
    console.log("FABRIC SELECTED:", fabric);
    onChange(idx, {
      ...row,
      fabric_name: fabric.fabric_name,
      fabric_master_id: fabric.id,
      composition: fabric.composition || '',
      gsm: fabric.gsm || '',
      dia_width: fabric.dia_width || '',
      unit: fabric.unit || 'KG',
      rate: fabric.standard_rate || '',
    });
  };
  const amount = (parseFloat(row.quantity) || 0) * (parseFloat(row.rate) || 0);
  return (
    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
      <td style={{ padding: '6px 8px', color: '#aaa', fontSize: 12, width: 28 }}>{idx + 1}</td>
      <td style={{ padding: '4px 6px', minWidth: 200 }}>
        <FabricAutocomplete value={row.fabric_name} onSelect={handleFabricSelect} placeholder="Select fabric..." />
      </td>
      <td style={{ padding: '4px 6px', minWidth: 160 }}>
        <input style={inputStyle} value={row.composition || ''} onChange={e => update('composition', e.target.value)} placeholder="77% PES / 23% COT" />
      </td>
      <td style={{ padding: '4px 6px', width: 70 }}>
        <input style={inputStyle} type="number" value={row.gsm || ''} onChange={e => update('gsm', e.target.value)} placeholder="270" />
      </td>
      <td style={{ padding: '4px 6px', width: 80 }}>
        <input style={inputStyle} value={row.dia_width || ''} onChange={e => update('dia_width', e.target.value)} placeholder='72"' />
      </td>
      <td style={{ padding: '4px 6px', width: 80 }}>
        <input style={inputStyle} type="number" value={row.quantity || ''} onChange={e => update('quantity', e.target.value)} placeholder="0.00" />
      </td>
      <td style={{ padding: '4px 6px', width: 70 }}>
        <select style={inputStyle} value={row.unit || 'KG'} onChange={e => update('unit', e.target.value)}>
          {['KG', 'MTR', 'YD'].map(u => <option key={u}>{u}</option>)}
        </select>
      </td>
      <td style={{ padding: '4px 6px', width: 90 }}>
        <input style={inputStyle} type="number" value={row.rate || ''} onChange={e => update('rate', e.target.value)} placeholder="₹/unit" />
      </td>
      <td style={{ padding: '6px 8px', width: 90, textAlign: 'right', fontWeight: 600, fontSize: 13 }}>
        ₹{amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </td>
      <td style={{ padding: '4px 6px', width: 32, textAlign: 'center' }}>
        <button type="button" onClick={() => onRemove(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ccc', fontSize: 16, lineHeight: 1 }}>✕</button>
      </td>
    </tr>
  );
}

function TrimRow({ row, idx, onChange, onRemove, trimsMaster }) {
  const update = (field, val) => onChange(idx, { ...row, [field]: val });
  const amount = (parseFloat(row.quantity) || 0) * (parseFloat(row.rate) || 0);
  const handleTrimSelect = (e) => {
    const trim = trimsMaster.find(t => t.id === parseInt(e.target.value));
    if (trim) onChange(idx, { ...row, trim_master_id: trim.id, item_name: trim.item_name, unit: trim.unit || 'PC', rate: trim.standard_rate || '' });
  };
  return (
    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
      <td style={{ padding: '6px 8px', color: '#aaa', fontSize: 12, width: 28 }}>{idx + 1}</td>
      <td style={{ padding: '4px 6px', minWidth: 180 }}>
        <select style={inputStyle} value={row.trim_master_id || ''} onChange={handleTrimSelect}>
          <option value="">Select trim...</option>
          {trimsMaster.map(t => <option key={t.id} value={t.id}>{t.item_name}</option>)}
        </select>
      </td>
      <td style={{ padding: '4px 6px', minWidth: 120 }}>
        <input style={inputStyle} value={row.item_name || ''} onChange={e => update('item_name', e.target.value)} placeholder="Item name" />
      </td>
      <td style={{ padding: '4px 6px', width: 80 }}>
        <input style={inputStyle} type="number" value={row.quantity || ''} onChange={e => update('quantity', e.target.value)} placeholder="0" />
      </td>
      <td style={{ padding: '4px 6px', width: 70 }}>
        <select style={inputStyle} value={row.unit || 'PC'} onChange={e => update('unit', e.target.value)}>
          {['PC', 'ROL', 'BOX', 'KG', 'MTR', 'SET'].map(u => <option key={u}>{u}</option>)}
        </select>
      </td>
      <td style={{ padding: '4px 6px', width: 90 }}>
        <input style={inputStyle} type="number" value={row.rate || ''} onChange={e => update('rate', e.target.value)} placeholder="₹/unit" />
      </td>
      <td style={{ padding: '6px 8px', width: 90, textAlign: 'right', fontWeight: 600, fontSize: 13 }}>
        ₹{amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </td>
      <td style={{ padding: '4px 6px', width: 32, textAlign: 'center' }}>
        <button type="button" onClick={() => onRemove(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ccc', fontSize: 16, lineHeight: 1 }}>✕</button>
      </td>
    </tr>
  );
}

const emptyFabricRow = () => ({ _id: Date.now() + Math.random(), fabric_master_id: null, fabric_name: '', composition: '', gsm: '', dia_width: '', quantity: '', unit: 'KG', rate: '' });
const emptyTrimRow = () => ({ _id: Date.now() + Math.random(), trim_master_id: null, item_name: '', quantity: '', unit: 'PC', rate: '' });

export default function CostEstimation() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [trimsMaster, setTrimsMaster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterBuyer, setFilterBuyer] = useState('');
  const [showSheet, setShowSheet] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [sheetLoading, setSheetLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fabricLines, setFabricLines] = useState([emptyFabricRow()]);
  const [trimLines, setTrimLines] = useState([emptyTrimRow()]);
  const [costs, setCosts] = useState({ cmt_per_pc: '', overhead_pct: '10', freight_per_pc: '', rejection_pct: '3', agent_commission_pct: '', target_margin_pct: '20', selling_price: '', notes: '' });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [ordRes, buyRes, trimRes] = await Promise.all([api.get('/orders'), api.get('/buyers'), api.get('/oe/trims-master')]);
      setOrders(ordRes.data.data || []);
      setBuyers(buyRes.data.data || []);
      setTrimsMaster(trimRes.data.data || []);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const openSheet = async (order) => {
    setSelectedOrder(order); setError(''); setSheetLoading(true); setShowSheet(true);
    try {
      const r = await api.get('/cost-estimation/' + order.id).catch(() => null);
      const data = r?.data?.data;
      if (data) {
        setFabricLines(data.fabrics?.length ? data.fabrics : [emptyFabricRow()]);
        setTrimLines(data.trims?.length ? data.trims : [emptyTrimRow()]);
        setCosts({ cmt_per_pc: data.cmt_per_pc || '', overhead_pct: data.overhead_pct || '10', freight_per_pc: data.freight_per_pc || '', rejection_pct: data.rejection_pct || '3', agent_commission_pct: data.agent_commission_pct || '', target_margin_pct: data.target_margin_pct || '20', selling_price: data.selling_price || order.price_per_pc || '', notes: data.notes || '' });
      } else {
        setFabricLines([emptyFabricRow()]); setTrimLines([emptyTrimRow()]);
        setCosts({ cmt_per_pc: '', overhead_pct: '10', freight_per_pc: '', rejection_pct: '3', agent_commission_pct: '', target_margin_pct: '20', selling_price: order.price_per_pc || '', notes: '' });
      }
    } finally { setSheetLoading(false); }
  };

  const updateFabricLine = (idx, updated) => setFabricLines(prev => prev.map((r, i) => i === idx ? updated : r));
  const removeFabricLine = (idx) => setFabricLines(prev => prev.filter((_, i) => i !== idx));
  const updateTrimLine = (idx, updated) => setTrimLines(prev => prev.map((r, i) => i === idx ? updated : r));
  const removeTrimLine = (idx) => setTrimLines(prev => prev.filter((_, i) => i !== idx));

  const qty = parseFloat(selectedOrder?.quantity) || 1;
  const fabricTotal = fabricLines.reduce((s, r) => s + (parseFloat(r.quantity) || 0) * (parseFloat(r.rate) || 0), 0);
  const trimTotal = trimLines.reduce((s, r) => s + (parseFloat(r.quantity) || 0) * (parseFloat(r.rate) || 0), 0);
  const cmtTotal = (parseFloat(costs.cmt_per_pc) || 0) * qty;
  const rawCogs = fabricTotal + trimTotal + cmtTotal;
  const overheadAmt = rawCogs * (parseFloat(costs.overhead_pct) || 0) / 100;
  const rejectionAmt = rawCogs * (parseFloat(costs.rejection_pct) || 0) / 100;
  const freightTotal = (parseFloat(costs.freight_per_pc) || 0) * qty;
  const totalCogs = rawCogs + overheadAmt + rejectionAmt + freightTotal;
  const totalCogsPerPc = qty > 0 ? totalCogs / qty : 0;
  const sellingPrice = parseFloat(costs.selling_price) || 0;
  const revenue = sellingPrice * qty;
  const agentAmt = revenue * (parseFloat(costs.agent_commission_pct) || 0) / 100;
  const netRevenue = revenue - agentAmt;
  const grossProfit = netRevenue - totalCogs;
  const grossMarginPct = netRevenue > 0 ? (grossProfit / netRevenue * 100) : 0;
  const targetMargin = parseFloat(costs.target_margin_pct) || 20;
  const marginOk = grossMarginPct >= targetMargin;

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await api.post('/cost-estimation/' + selectedOrder.id, {
        fabrics: fabricLines.filter(r => r.fabric_name), trims: trimLines.filter(r => r.item_name),
        ...costs, fabric_cost: fabricTotal, trim_cost: trimTotal, cmt_cost: cmtTotal,
        overhead_cost: overheadAmt, freight_cost: freightTotal, rejection_loss: rejectionAmt,
        total_cogs: totalCogs, total_cogs_per_pc: totalCogsPerPc,
        gross_profit: grossProfit, gross_margin_pct: grossMarginPct.toFixed(2),
      });
      setShowSheet(false); fetchAll();
    } catch (err) { setError(err.response?.data?.error || 'Failed to save cost sheet'); }
    finally { setSaving(false); }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };
  const filtered = orders.filter(o => {
    const q = search.toLowerCase();
    return (!q || o.order_no?.toLowerCase().includes(q) || o.buyer_name?.toLowerCase().includes(q) || o.style_no?.toLowerCase().includes(q))
      && (!filterBuyer || o.buyer_id === parseInt(filterBuyer));
  });
  const fmt = (n) => '₹' + parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtInt = (n) => '₹' + parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const marginBadge = (pct) => { const p = parseFloat(pct); if (p >= 25) return { bg: '#EEEDF8', color: '#2A2970' }; if (p >= 15) return { bg: '#FAEEDA', color: '#854F0B' }; return { bg: '#FCEBEB', color: '#A32D2D' }; };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f2f8' }}>
      <p style={{ color: '#666' }}>Loading Cost Estimation...</p>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'transparent', fontFamily: 'inherit' }}>
      

      <div style={{ padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1a1a2e' }}>Cost Estimation</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#888' }}>Build detailed cost sheets per order — fabrics, trims, CMT, overhead</p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search order / buyer / style..." style={{ padding: '7px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, width: 240 }} />
            <select value={filterBuyer} onChange={e => setFilterBuyer(e.target.value)} style={{ padding: '7px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}>
              <option value="">All Buyers</option>
              {buyers.map(b => <option key={b.id} value={b.id}>{b.buyer_name}</option>)}
            </select>
          </div>
        </div>

        <div style={{ background: 'white', borderRadius: 10, boxShadow: 'none', border: '1px solid #E8E7E7', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F7F6F3' }}>
                {['Order No', 'Buyer', 'Style', 'Qty', 'Selling Price', 'COGS/pc', 'Margin', 'Status', 'Cost Sheet'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: '#666', fontWeight: 600, fontSize: 11, borderBottom: '2px solid #eee', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: '#aaa' }}>No orders found</td></tr>
              ) : filtered.map(order => {
                const mb = order.gross_margin_pct ? marginBadge(order.gross_margin_pct) : null;
                return (
                  <tr key={order.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 600, color: '#3B3A8F' }}>{order.order_no}</td>
                    <td style={{ padding: '10px 14px' }}>{order.buyer_name}</td>
                    <td style={{ padding: '10px 14px', color: '#666' }}>{order.style_no}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right' }}>{parseInt(order.quantity || 0).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right' }}>{order.price_per_pc ? fmt(order.price_per_pc) : '—'}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: order.total_cogs_per_pc ? '#E24B4A' : '#aaa' }}>{order.total_cogs_per_pc ? fmt(order.total_cogs_per_pc) : '—'}</td>
                    <td style={{ padding: '10px 14px' }}>
                      {mb ? <span style={{ background: mb.bg, color: mb.color, padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{parseFloat(order.gross_margin_pct).toFixed(1)}%</span>
                        : <span style={{ color: '#ccc', fontSize: 12 }}>Not costed</span>}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 500, background: order.status === 'confirmed' ? '#EEEDF8' : order.status === 'in_production' ? '#FAEEDA' : '#F1EFE8', color: order.status === 'confirmed' ? '#2A2970' : order.status === 'in_production' ? '#854F0B' : '#5F5E5A' }}>{order.status?.replace('_', ' ') || 'sample'}</span>
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <button onClick={() => openSheet(order)} style={{ padding: '5px 14px', background: order.total_cogs_per_pc ? '#EEEDF8' : '#3B3A8F', color: order.total_cogs_per_pc ? '#3B3A8F' : 'white', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                        {order.total_cogs_per_pc ? '✏️ Edit' : '+ Build'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <ListFooter shown={filtered.length} total={orders.length} noun="orders" />
        </div>
      </div>

      {showSheet && selectedOrder && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, overflowY: 'auto', padding: '24px 0' }}>
          <div style={{ background: 'white', borderRadius: 14, width: '92%', maxWidth: 1100, boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
            <div style={{ background: '#2D2C72', padding: '20px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ color: '#fff', margin: 0, fontSize: 17, color: 'rgba(255,255,255,0.72)', fontWeight: 700 }}>Cost Sheet — {selectedOrder.order_no}</h3>
                <p style={{ margin: '3px 0 0', fontSize: 13, color: 'rgba(255,255,255,0.72)' }}>{selectedOrder.buyer_name} · {selectedOrder.style_no} · {parseInt(selectedOrder.quantity || 0).toLocaleString('en-IN')} pcs</p>
              </div>
              <button onClick={() => setShowSheet(false)} style={{ background: 'none', border: 'none', fontSize: 22, color: 'rgba(255,255,255,0.85)', cursor: 'pointer' }}>✕</button>
            </div>
            {sheetLoading ? (
              <div style={{ padding: 60, textAlign: 'center', color: '#aaa' }}>Loading cost data...</div>
            ) : (
              <form onSubmit={handleSave}>
                <div style={{ padding: '20px 28px', display: 'grid', gridTemplateColumns: '1fr 280px', gap: 24 }}>
                  <div>
                    <div style={{ marginBottom: 28 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 0.5 }}>🧵 Fabrics</h4>
                        <span style={{ fontSize: 12, color: '#888' }}>Total: <strong style={{ color: '#E22D2D' }}>{fmtInt(fabricTotal)}</strong></span>
                      </div>
                      <div style={{ border: '1px solid #eee', borderRadius: 8, overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr style={{ background: '#F7F6F3' }}>
                              {['#', 'Fabric Name', 'Composition', 'GSM', 'DIA/Width', 'Qty', 'Unit', 'Rate (₹)', 'Amount', ''].map(h => (
                                <th key={h} style={{ padding: '7px 8px', textAlign: 'left', color: '#888', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', borderBottom: '1px solid #eee' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {fabricLines.map((row, idx) => (
                              <FabricRow key={row._id || idx} row={row} idx={idx} onChange={updateFabricLine} onRemove={removeFabricLine} />
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <button type="button" onClick={() => setFabricLines(prev => [...prev, emptyFabricRow()])} style={{ marginTop: 8, padding: '5px 14px', background: '#EEEDF8', color: '#3B3A8F', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>+ Add Fabric</button>
                    </div>

                    <div style={{ marginBottom: 28 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 0.5 }}>🪡 Trims & Accessories</h4>
                        <span style={{ fontSize: 12, color: '#888' }}>Total: <strong style={{ color: '#E22D2D' }}>{fmtInt(trimTotal)}</strong></span>
                      </div>
                      <div style={{ border: '1px solid #eee', borderRadius: 8, overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr style={{ background: '#F7F6F3' }}>
                              {['#', 'Item (from Master)', 'Item Name', 'Qty', 'Unit', 'Rate (₹)', 'Amount', ''].map(h => (
                                <th key={h} style={{ padding: '7px 8px', textAlign: 'left', color: '#888', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', borderBottom: '1px solid #eee' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {trimLines.map((row, idx) => (
                              <TrimRow key={row._id || idx} row={row} idx={idx} onChange={updateTrimLine} onRemove={removeTrimLine} trimsMaster={trimsMaster} />
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <button type="button" onClick={() => setTrimLines(prev => [...prev, emptyTrimRow()])} style={{ marginTop: 8, padding: '5px 14px', background: '#EEEDF8', color: '#3B3A8F', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>+ Add Trim</button>
                    </div>

                    <div>
                      <h4 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 0.5 }}>⚙️ Other Costs</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                        <div><label style={labelStyle}>CMT per pc (₹)</label><input style={inputStyle} type="number" step="0.01" value={costs.cmt_per_pc} onChange={e => setCosts(p => ({ ...p, cmt_per_pc: e.target.value }))} placeholder="45.00" /></div>
                        <div><label style={labelStyle}>Overhead (%)</label><input style={inputStyle} type="number" step="0.1" value={costs.overhead_pct} onChange={e => setCosts(p => ({ ...p, overhead_pct: e.target.value }))} placeholder="10" /></div>
                        <div><label style={labelStyle}>Freight per pc (₹)</label><input style={inputStyle} type="number" step="0.01" value={costs.freight_per_pc} onChange={e => setCosts(p => ({ ...p, freight_per_pc: e.target.value }))} placeholder="12.00" /></div>
                        <div><label style={labelStyle}>Rejection Loss (%)</label><input style={inputStyle} type="number" step="0.1" value={costs.rejection_pct} onChange={e => setCosts(p => ({ ...p, rejection_pct: e.target.value }))} placeholder="3" /></div>
                        <div><label style={labelStyle}>Agent Commission (%)</label><input style={inputStyle} type="number" step="0.1" value={costs.agent_commission_pct} onChange={e => setCosts(p => ({ ...p, agent_commission_pct: e.target.value }))} placeholder="5" /></div>
                        <div><label style={labelStyle}>Target Margin (%)</label><input style={inputStyle} type="number" step="0.1" value={costs.target_margin_pct} onChange={e => setCosts(p => ({ ...p, target_margin_pct: e.target.value }))} placeholder="20" /></div>
                      </div>
                      <div style={{ marginTop: 12 }}>
                        <label style={labelStyle}>Notes / Remarks</label>
                        <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 56 }} value={costs.notes} onChange={e => setCosts(p => ({ ...p, notes: e.target.value }))} placeholder="Any costing notes..." />
                      </div>
                    </div>
                  </div>

                  <div>
                    <div style={{ position: 'sticky', top: 24 }}>
                      <h4 style={{ margin: '0 0 14px', fontSize: 13, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 0.5 }}>📊 Cost Summary</h4>
                      <div style={{ marginBottom: 16 }}>
                        <label style={labelStyle}>Selling Price / pc (₹)</label>
                        <input style={{ ...inputStyle, fontSize: 16, fontWeight: 700, color: '#3B3A8F', borderColor: '#3B3A8F' }} type="number" step="0.01" value={costs.selling_price} onChange={e => setCosts(p => ({ ...p, selling_price: e.target.value }))} placeholder="0.00" />
                      </div>
                      <div style={{ background: '#F7F6F3', borderRadius: 10, padding: '14px 16px', fontSize: 13 }}>
                        {[
                          { label: 'Fabric Cost', value: fmtInt(fabricTotal), sub: true },
                          { label: 'Trim Cost', value: fmtInt(trimTotal), sub: true },
                          { label: 'CMT Labour', value: fmtInt(cmtTotal), sub: true },
                          { label: `Overhead (${costs.overhead_pct || 0}%)`, value: fmtInt(overheadAmt), sub: true },
                          { label: `Rejection (${costs.rejection_pct || 0}%)`, value: fmtInt(rejectionAmt), sub: true },
                          { label: 'Freight', value: fmtInt(freightTotal), sub: true },
                          { label: 'Total COGS', value: fmtInt(totalCogs), bold: true, sep: true },
                          { label: 'COGS / pc', value: fmt(totalCogsPerPc), color: '#E24B4A' },
                          { label: 'Revenue (ex-commission)', value: fmtInt(netRevenue), sep: true },
                          { label: 'Gross Profit', value: fmtInt(grossProfit), color: grossProfit >= 0 ? '#166534' : '#A32D2D', bold: true },
                        ].map((row, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: row.sep ? '8px 0 4px' : '4px 0', borderTop: row.sep ? '1px solid #e0e0e0' : 'none', marginTop: row.sep ? 6 : 0 }}>
                            <span style={{ color: row.sub ? '#888' : '#555', fontSize: row.sub ? 12 : 13, paddingLeft: row.sub ? 10 : 0 }}>{row.label}</span>
                            <span style={{ fontWeight: row.bold ? 700 : 500, color: row.color || '#222', fontSize: row.bold ? 14 : 13 }}>{row.value}</span>
                          </div>
                        ))}
                      </div>
                      <div style={{ marginTop: 14, borderRadius: 10, padding: '14px 16px', textAlign: 'center', background: marginOk ? '#dcfce7' : grossMarginPct >= 10 ? '#FAEEDA' : '#FCEBEB', border: `1px solid ${marginOk ? '#86efac' : grossMarginPct >= 10 ? '#f6d195' : '#fca5a5'}` }}>
                        <div style={{ fontSize: 28, fontWeight: 800, color: marginOk ? '#166534' : grossMarginPct >= 10 ? '#854F0B' : '#A32D2D' }}>{grossMarginPct.toFixed(1)}%</div>
                        <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>Gross Margin</div>
                        <div style={{ fontSize: 11, marginTop: 6, color: marginOk ? '#166534' : '#A32D2D', fontWeight: 600 }}>{marginOk ? `✓ Above ${targetMargin}% target` : `✗ Below ${targetMargin}% target`}</div>
                      </div>
                      {error && <div style={{ marginTop: 12, background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, fontSize: 12 }}>{error}</div>}
                      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                        <button type="button" onClick={() => setShowSheet(false)} style={{ flex: 1, padding: '9px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                        <button type="submit" disabled={saving} style={{ flex: 2, padding: '9px', background: '#E22D2D', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 700 }}>{saving ? 'Saving...' : '💾 Save Cost Sheet'}</button>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
